import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Mic, X, Check } from 'lucide-react';

import styles from './styles/AudioRecorder.module.css';

const AudioRecorder = ({
    onAudioRecorded,
    onRecordingStateChange = () => {},
    onCancel = () => {},
}) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);

    // Refs
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerIntervalRef = useRef(null);
    const detectedMimeTypeRef = useRef('audio/webm');

    // Clean up function - doesn't call any callbacks
    const cleanupWithoutCallbacks = useCallback(() => {
        // Stop media tracks
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        // Stop timer
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }

        // Clear data
        audioChunksRef.current = [];

        // Clear recorder
        if (mediaRecorderRef.current) {
            try {
                if (mediaRecorderRef.current.state !== 'inactive') {
                    // Remove all event listeners to prevent callbacks
                    mediaRecorderRef.current.onstart = null;
                    mediaRecorderRef.current.onstop = null;
                    mediaRecorderRef.current.ondataavailable = null;
                    mediaRecorderRef.current.onerror = null;

                    // Force stop without triggering events
                    mediaRecorderRef.current.stop();
                }
            } catch (e) {
                console.error('Error stopping media recorder:', e);
            }

            mediaRecorderRef.current = null;
        }
    }, []);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            cleanupWithoutCallbacks();
        };
    }, [cleanupWithoutCallbacks]);

    // Start recording function
    const startRecording = useCallback(async () => {
        try {
            // First set recording state to true to immediately update UI
            setIsRecording(true);
            onRecordingStateChange(true);

            // Reset state
            setRecordingTime(0);
            audioChunksRef.current = [];

            // Request audio stream
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;

            // Determine supported MIME type
            let mimeType = 'audio/webm';
            const options = {};

            if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                options.mimeType = 'audio/webm;codecs=opus';
                mimeType = 'audio/webm';
            } else if (MediaRecorder.isTypeSupported('audio/mp4;codecs=opus')) {
                options.mimeType = 'audio/mp4;codecs=opus';
                mimeType = 'audio/mp4';
            } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                options.mimeType = 'audio/webm';
                mimeType = 'audio/webm';
            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                options.mimeType = 'audio/mp4';
                mimeType = 'audio/mp4';
            }

            // Store detected mime type in ref
            detectedMimeTypeRef.current = mimeType;

            // Create media recorder
            const recorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = recorder;

            // Set up data available handler
            recorder.ondataavailable = event => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            // Start recorder
            recorder.start(100);

            // Start timer
            timerIntervalRef.current = setInterval(() => {
                setRecordingTime(time => time + 1);
            }, 1000);
        } catch (error) {
            console.error('Failed to start recording:', error);
            setIsRecording(false);
            onRecordingStateChange(false);
            cleanupWithoutCallbacks();
        }
    }, [cleanupWithoutCallbacks, onRecordingStateChange]);

    // Complete recording function - sends audio for processing
    const completeRecording = useCallback(() => {
        if (!mediaRecorderRef.current || !streamRef.current) {
            setIsRecording(false);
            onRecordingStateChange(false);
            return;
        }

        try {
            // Create a local copy of chunks and other variables we need
            const chunks = [...audioChunksRef.current];
            const blobOptions = {
                type: mediaRecorderRef.current.mimeType || detectedMimeTypeRef.current,
            };

            // Clean up recorder and state first
            cleanupWithoutCallbacks();

            setIsRecording(false);
            onRecordingStateChange(false);
            setRecordingTime(0);

            // Only if we have audio data, create a blob and call the callback
            if (chunks.length > 0) {
                const audioBlob = new Blob(chunks, blobOptions);
                // Process the audio
                onAudioRecorded(audioBlob);
            }
        } catch (error) {
            console.error('Failed to complete recording:', error);
            setIsRecording(false);
            onRecordingStateChange(false);
            cleanupWithoutCallbacks();
        }
    }, [cleanupWithoutCallbacks, onRecordingStateChange, onAudioRecorded]);

    // Cancel recording function - discards the recording
    const cancelRecording = useCallback(() => {
        try {
            // Clean up without triggering processing
            cleanupWithoutCallbacks();

            // Update state
            setIsRecording(false);
            onRecordingStateChange(false);
            setRecordingTime(0);

            // Call cancel callback
            onCancel();
        } catch (error) {
            console.error('Failed to cancel recording:', error);
            setIsRecording(false);
            onRecordingStateChange(false);
        }
    }, [cleanupWithoutCallbacks, onRecordingStateChange, onCancel]);

    // Format time for display
    const formatTime = useCallback(seconds => {
        const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    }, []);

    return (
        <div className={styles.audioRecorder}>
            {!isRecording ? (
                <button
                    className={styles.recordButton}
                    onClick={startRecording}
                    aria-label="Start recording"
                >
                    <Mic size={18} />
                </button>
            ) : (
                <div className={styles.recordingControls}>
                    <div className={styles.recordingIndicator}>
                        <div className={styles.timer}>{formatTime(recordingTime)}</div>
                    </div>
                    <button
                        className={styles.recordButton}
                        onClick={cancelRecording}
                        aria-label="Cancel recording"
                    >
                        <X size={18} />
                    </button>
                    <button
                        className={styles.completeButton}
                        onClick={completeRecording}
                        aria-label="Complete recording"
                    >
                        <Check size={18} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default memo(AudioRecorder);
